import { useState } from 'react';
import type { Site, Unit } from '../hooks/useDashboardData';
import { api } from '../utils/api';
import './UnitLifecycleActions.css';

interface UnitLifecycleActionsProps {
  unit: Unit;
  sites: Site[];
  onComplete: () => void;
  compact?: boolean;
}

export function UnitLifecycleActions({ unit, sites, onComplete, compact = false }: UnitLifecycleActionsProps) {
  const [dialog, setDialog] = useState<'release' | 'move' | null>(null);
  const [toSiteId, setToSiteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const close = () => {
    if (busy) return;
    setDialog(null);
    setToSiteId('');
    setError('');
  };

  const release = async () => {
    if (!unit.deviceUuid) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/v1/devices/${unit.deviceUuid}/release`);
      setDialog(null);
      setToSiteId('');
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not release this device');
    } finally {
      setBusy(false);
    }
  };

  const move = async () => {
    if (!unit.deviceUuid || !toSiteId) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/v1/devices/${unit.deviceUuid}/move`, {
        toSiteId,
        nodeName: unit.nodeName,
      });
      setDialog(null);
      setToSiteId('');
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not move this device');
    } finally {
      setBusy(false);
    }
  };

  const hasLifecycleIdentity = Boolean(unit.deviceUuid);
  const destinationSites = sites.filter((site) => site.id !== unit.siteId);

  return (
    <div className={`unit-lifecycle-actions ${compact ? 'unit-lifecycle-actions--compact' : ''}`}>
      <button
        type="button"
        onClick={() => setDialog('move')}
        disabled={!hasLifecycleIdentity || destinationSites.length === 0}
        title={!hasLifecycleIdentity
          ? 'This unit was registered before device lifecycle tracking and cannot be moved.'
          : destinationSites.length === 0
            ? 'You need access to another site to move this device.'
            : undefined}
      >Move to site</button>
      <button
        type="button"
        className="unit-lifecycle-actions__danger"
        onClick={() => setDialog('release')}
        disabled={!hasLifecycleIdentity}
        title={!hasLifecycleIdentity ? 'This unit was registered before device lifecycle tracking and cannot be released.' : undefined}
      >Release device</button>

      {dialog && (
        <div className="unit-action-modal" role="dialog" aria-modal="true" onClick={close}>
          <div className="unit-action-modal__card" onClick={(event) => event.stopPropagation()}>
            {dialog === 'release' ? (
              <>
                <h2>Release {unit.nodeName}?</h2>
                <p>The device will stop publishing and become available for a future adoption. Existing telemetry remains with this site.</p>
                {error && <p className="unit-action-modal__error">{error}</p>}
                <div className="unit-action-modal__buttons">
                  <button type="button" onClick={close} disabled={busy}>Cancel</button>
                  <button type="button" className="unit-lifecycle-actions__danger" onClick={release} disabled={busy}>{busy ? 'Releasing…' : 'Release device'}</button>
                </div>
              </>
            ) : (
              <>
                <h2>Move {unit.nodeName}</h2>
                <p>Moving creates a new binding. The current site's telemetry epoch remains unchanged.</p>
                <label>Destination site
                  <select value={toSiteId} onChange={(event) => setToSiteId(event.target.value)} disabled={busy}>
                    <option value="">Choose a site…</option>
                    {destinationSites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
                  </select>
                </label>
                {error && <p className="unit-action-modal__error">{error}</p>}
                <div className="unit-action-modal__buttons">
                  <button type="button" onClick={close} disabled={busy}>Cancel</button>
                  <button
                    type="button"
                    onClick={move}
                    disabled={busy || !toSiteId}
                    title={!toSiteId ? 'Choose a destination site first.' : undefined}
                  >{busy ? 'Moving…' : 'Move device'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UnitLifecycleActions;
